/**
 * Hook per leggere la history prezzo di un materiale.
 *
 * Restituisce gli snapshot ordinati cronologicamente. Il trigger DB
 * `trg_log_material_price_change` produce una riga in
 * `material_price_history` ogni volta che cambia `list_price`,
 * `unit_price` o `extra_discount` su `materials`.
 *
 * Usato dal chart sparkline / tooltip "questo materiale è salito X% in N mesi".
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PriceHistoryPoint {
  id: string;
  list_price: number | null;
  unit_price: number | null;
  extra_discount: number | null;
  changed_at: string;
  source: string;
}

export interface PriceTrend {
  /** Variazione % unit_price tra primo e ultimo punto della finestra. */
  changePct: number;
  /** Mesi coperti dalla finestra (min 1). */
  monthsCovered: number;
  /** "up", "down" o "flat" (entro ±1%). */
  direction: 'up' | 'down' | 'flat';
  /** Primo unit_price utile. */
  firstPrice: number;
  /** Ultimo unit_price utile. */
  lastPrice: number;
}

export function useMaterialPriceHistory(materialId: string | null | undefined) {
  return useQuery<PriceHistoryPoint[]>({
    queryKey: ['material-price-history', materialId],
    enabled: !!materialId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      if (!materialId) return [];
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('material_price_history' as any)
        .select('id, list_price, unit_price, extra_discount, changed_at, source')
        .eq('material_id', materialId)
        .order('changed_at', { ascending: true })
        .limit(500);
      if (error) {
        console.error('[useMaterialPriceHistory] error:', error);
        return [];
      }
      return (data as unknown as PriceHistoryPoint[]) ?? [];
    },
  });
}

/**
 * Calcola il trend su unit_price dato un array di punti.
 * Restituisce null se troppi pochi punti o nessuno con prezzo valorizzato.
 */
export function computePriceTrend(points: PriceHistoryPoint[]): PriceTrend | null {
  const usable = points.filter((p) => p.unit_price != null && Number.isFinite(Number(p.unit_price)));
  if (usable.length < 2) return null;

  const first = usable[0];
  const last = usable[usable.length - 1];
  const firstPrice = Number(first.unit_price);
  const lastPrice = Number(last.unit_price);
  if (firstPrice <= 0) return null;

  const changePct = ((lastPrice - firstPrice) / firstPrice) * 100;
  const firstDate = new Date(first.changed_at).getTime();
  const lastDate = new Date(last.changed_at).getTime();
  const monthsCovered = Math.max(
    1,
    (lastDate - firstDate) / (1000 * 60 * 60 * 24 * 30),
  );

  let direction: PriceTrend['direction'] = 'flat';
  if (changePct > 1) direction = 'up';
  else if (changePct < -1) direction = 'down';

  return {
    changePct,
    monthsCovered,
    direction,
    firstPrice,
    lastPrice,
  };
}
